<template>
  <v-form
    :class="{ 'bg-white': !hideAdmin }"
    @submit.prevent="() => (filteredOrganizations.length ? loginOrganization(filteredOrganizations[0]!._id) : undefined)"
  >
    <v-text-field
      v-model.lazy="searchString"
      :placeholder="$t('search')"
      aria-autocomplete="none"
      autocomplete="off"
      autofocus
      class="mb-2"
      clearable
      density="comfortable"
      hide-details
      prepend-inner-icon="search"
      single-line
      :label="$t('search')"
    />

    <div v-if="organizations.length === 0" class="text-medium-emphasis text-center">{{ $t("noOrganizations") }}</div>
    <div v-else-if="filteredOrganizations.length === 0" class="text-medium-emphasis text-center">{{ $t("noOrganizationsWithFilter") }}</div>

    <v-virtual-scroll
      ref="virtualScroll"
      :class="{ 'v-theme--light': !hideAdmin }"
      style="height: 40vh"
      :items="filteredOrganizations"
      item-height="54"
      item-key="_id"
    >
      <template #default="{ item: organization }">
        <v-list-item
          :key="organization._id"
          class="rounded px-3"
          height="54"
          :base-color="hoverItemId === organization._id ? 'primary' : undefined"
          @click="() => loginOrganization(organization._id)"
          @mouseenter="() => (hoverItemId = organization._id)"
          @mouseleave="
            () => {
              if (hoverItemId === organization._id) {
                hoverItemId = null
              }
            }
          "
        >
          <template #prepend>
            <organization-icon :organization="organization" />
          </template>

          <template #title>{{ organization.description }}</template>

          <template v-if="organization.lastLogin" #subtitle>
            <v-icon icon="history" size="14px" class="mr-1" />
            <span>{{ dateToReadableTimePast($i18n, organization.lastLogin) }}</span>
          </template>

          <template #append>
            <v-icon v-if="isSuperAdmin" class="mr-2" :icon="`$domain_${organization.domain.toLowerCase()}`" size="1.25rem" />

            <v-icon icon="arrow_forward" :class="hoverItemId === organization._id ? 'arrow-forward--active' : ''" class="arrow-forward" />
          </template>
        </v-list-item>
      </template>
    </v-virtual-scroll>

    <v-card-actions v-if="currentUser?.userRole === UserRole.SUPER_ADMIN && !hideAdmin">
      <v-btn class="text-none" block color="admin" variant="tonal" prepend-icon="admin_panel_settings" @click="loginAdmin()">{{
        $t("admin.title")
      }}</v-btn>
    </v-card-actions>

    <inner-loading :showing="pending || loggingIn" :label="pending ? '' : 'Logging in...'" />
  </v-form>
</template>

<script setup lang="ts">
import sortBy from "lodash-es/sortBy"
import type { VVirtualScroll } from "vuetify/components"

const props = defineProps<{ hideAdmin?: boolean }>()
const { hideAdmin } = toRefs(props)

const $i18n = useI18n()
const $route = useRoute()
const $router = useRouter()

const mainStore = useMainStore()
const authStore = useAuthStore()
const userStore = useUserStore()
const organizationStore = useOrganizationStore()

const { pending: pendingAuth, error, isSuperAdmin } = storeToRefs(authStore)
const { currentUser } = storeToRefs(userStore)
const virtualScroll = ref<InstanceType<typeof VVirtualScroll> | null>(null)

const { data: organizations, status: organizationsStatus } = useLazyAsyncData(
  "org_list",
  async () => organizationStore.getOrganizations(),
  { default: () => [] as Array<Organization> },
)

const { status: currentUserStatus } = useLazyAsyncData(
  "org_list_user",
  async () => !!currentUser.value || (await userStore.getCurrentUser()),
)

const searchString = ref<string | null>(null)
const hoverItemId = ref<string | null>(null)

const pending = computed(() => pendingAuth.value || organizationsStatus.value === "pending" || currentUserStatus.value === "pending")
const loggingIn = ref(false)

const filteredOrganizations = computed(() => {
  const searchText = searchString.value?.toLowerCase()

  const mappedOrganizations = organizations.value
    .filter((org?: Organization) => {
      // No one can login to LABEL.
      if (org?.type === OrganizationType.LABEL) return false

      // Filter by search text - if any.
      if (searchText) return org?.description?.toLowerCase().includes(searchText) || org?._id === searchText

      return true
    })
    .map((org) => ({
      ...org,
      lastLogin: currentUser.value?.lastLogins.find((s) => s.organization === org?._id)?.date,
    }))

  return sortBy(mappedOrganizations, (org) => org.lastLogin && -new Date(org.lastLogin).valueOf())
})

async function loginAdmin() {
  if (!isSuperAdmin.value) return

  await authStore.logoutOrganization()

  $router.push({ name: "admin" })
}

async function loginOrganization(organizationId: string) {
  await authStore.updateOrganization(organizationId)

  if (error.value) {
    mainStore.notify({
      title: $i18n.t("error"),
      text: $i18n.t(error.value),
      type: "error",
      closable: true,
    })

    return
  }

  loggingIn.value = true

  setTimeout(() => {
    if ($route.name !== "dashboard") {
      // Organization selected: go to dashboard or follow redirect
      // Verify if redirect is a relative path (starts with "/") to avoid open redirect vulnerabilities
      if ($route.query.redirect) {
        const redirect = $route.query.redirect as string

        // Only allow url's that start with / and not //
        if (redirect.startsWith("//") || !redirect.startsWith("/")) {
          $router.push("/dashboard")
          return
        }

        // Redirect to the page that was requested before login
        location.href = $route.query.redirect as string
      } else {
        // No redirect: go to dashboard
        // Use location.href to make sure the page is fully reloaded
        location.href = "/dashboard"
      }
    } else {
      // Organization selected and already at dashboard: reload dashboard
      location.reload()
    }

    loggingIn.value = false
  }, 500)
}

watch([organizationsStatus, currentUser], () => {
  if (organizationsStatus.value === "pending" || !currentUser.value) {
    return
  }

  if (organizations.value.length === 1 && currentUser.value?.userRole !== UserRole.SUPER_ADMIN) {
    loginOrganization(organizations.value[0]!._id)
  }
})

watch(filteredOrganizations, () => virtualScroll.value?.scrollToIndex(0))
</script>

<style>
.arrow-forward {
  transition: transform 0.05s ease-in-out;
}

.arrow-forward--active {
  transform: translateX(4px);
}
</style>
